/**
 * D90 — Connected Pages bulk management panel.
 *
 * Renders below the Facebook integration card on the Integrations page
 * when the workspace has connected Pages. Replaces the previous
 * "scroll through 134 pages inline" UX with a proper bulk-management
 * surface: search/filter, row selection (incl. "Select all matching
 * the current filter"), and a bulk-assign bar that targets a Client
 * Group (existing or freshly-created inline).
 *
 * Defensive rules (do not regress):
 *   - NONE_SENTINEL ('__none__') for the "Unassign" SelectItem; never
 *     an empty string (Radix crashes on those).
 *   - Null-safe access on every page field.
 *   - Loading / error / empty states with retry — no infinite spinners.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { socialAPI, clientGroupAPI } from '../lib/api';
import { sortClientsAlpha } from '../lib/sortClients';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { StellarSelect } from './StellarFormControls';
import {
  Search, Loader2, AlertTriangle, Instagram, Image as ImageIcon,
  Layers, Plus, X, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const NONE_SENTINEL = '__none__';

export function ConnectedPagesPanel({ workspaceId, onChanged }) {
  // ── Data state ─────────────────────────────────────────────
  const [pages, setPages] = useState([]); // [{id, name, ig_username, client_group_id, friendly_name, ...}]
  const [clientGroups, setClientGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── UI state ───────────────────────────────────────────────
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState({}); // {page_id: true}
  const [assignTarget, setAssignTarget] = useState(NONE_SENTINEL); // CG id OR NONE_SENTINEL OR '__create__'
  const [newCgName, setNewCgName] = useState('');
  const [busy, setBusy] = useState(false);

  // ── Load ──────────────────────────────────────────────────
  const load = useCallback(() => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      socialAPI.listConnections(workspaceId).then((r) => r?.data ?? null).catch((e) => {
        throw e?.response?.data?.detail || e?.message || 'Failed to load Pages';
      }),
      clientGroupAPI.list(workspaceId).then((r) => Array.isArray(r?.data) ? r.data : []).catch(() => []),
    ])
      .then(([conn, cgs]) => {
        const all = Array.isArray(conn?.pages) ? conn.pages : [];
        setPages(all);
        setClientGroups(sortClientsAlpha(cgs || []));
        setSelected({});
        setLoading(false);
      })
      .catch((e) => {
        setError(typeof e === 'string' ? e : 'Failed to load Pages');
        setLoading(false);
      });
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  // ── Derived ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return pages;
    return pages.filter((p) =>
      ((p?.name) || '').toLowerCase().includes(f) ||
      ((p?.ig_username) || '').toLowerCase().includes(f) ||
      ((p?.category) || '').toLowerCase().includes(f) ||
      ((p?.friendly_name) || '').toLowerCase().includes(f),
    );
  }, [pages, filter]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selected[p?.id]);
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedCount = selectedIds.length;
  const cgById = useMemo(() => {
    const m = {};
    clientGroups.forEach((g) => { if (g?.id) m[g.id] = g; });
    return m;
  }, [clientGroups]);

  const assignOptions = useMemo(() => ([
    { value: NONE_SENTINEL, label: 'Unassign (clear client group)' },
    ...clientGroups.map((g) => ({ value: g?.id || NONE_SENTINEL, label: g?.name || 'Unnamed' })),
    { value: '__create__', label: '+ Create new client group…' },
  ]), [clientGroups]);

  // ── Mutations ─────────────────────────────────────────────
  const toggleRow = (id, checked) => {
    setSelected((s) => ({ ...s, [id]: checked }));
  };

  const toggleAllFiltered = () => {
    const next = { ...selected };
    if (allFilteredSelected) {
      filtered.forEach((p) => { if (p?.id) delete next[p.id]; });
    } else {
      filtered.forEach((p) => { if (p?.id) next[p.id] = true; });
    }
    setSelected(next);
  };

  const clearSelection = () => setSelected({});

  const applyAssignment = async () => {
    if (selectedCount === 0) {
      toast.error('Pick at least one Page to assign');
      return;
    }
    if (assignTarget === '__create__' && !newCgName.trim()) {
      toast.error('Enter a name for the new client group');
      return;
    }
    setBusy(true);
    try {
      let target_cg_id = assignTarget === NONE_SENTINEL ? null : assignTarget;
      let target_cg_name = '';

      // Inline create: hit clientGroupAPI.create first, then assign.
      if (assignTarget === '__create__') {
        const createRes = await clientGroupAPI.create({
          workspace_id: workspaceId,
          name: newCgName.trim(),
        });
        const created = createRes?.data;
        target_cg_id = created?.id;
        target_cg_name = created?.name || newCgName.trim();
        if (!target_cg_id) throw new Error('Created client group missing id');
        setClientGroups((arr) => sortClientsAlpha([...arr, created]));
      } else if (target_cg_id) {
        target_cg_name = cgById[target_cg_id]?.name || '';
      }

      const r = await socialAPI.bulkAssign({
        workspace_id: workspaceId,
        account_ids: selectedIds,
        client_group_id: target_cg_id,
      });
      const count = r?.data?.updated_count ?? selectedCount;
      if (target_cg_id) {
        toast.success(`Assigned ${count} Page${count === 1 ? '' : 's'} to ${target_cg_name}`);
      } else {
        toast.success(`Unassigned ${count} Page${count === 1 ? '' : 's'}`);
      }
      setNewCgName('');
      setAssignTarget(NONE_SENTINEL);
      // Reload to reflect new chips and notify parent.
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to assign');
    } finally {
      setBusy(false);
    }
  };

  // ── Render ────────────────────────────────────────────────
  if (!workspaceId) return null;

  return (
    <Card className="mt-4" data-testid="d90-connected-pages-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="h-4 w-4 text-gold" />
              Connected Pages
              <Badge variant="outline" className="text-[10px]" data-testid="d90-total-pages">
                {pages.length}
              </Badge>
            </CardTitle>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Search, select, and bulk-assign your Facebook Pages (and their linked Instagram accounts) to a client.
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter pages…"
              className="h-8 pl-7 text-[12px]"
              data-testid="d90-filter"
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-8" data-testid="d90-loading">
            <Loader2 className="h-5 w-5 animate-spin text-gold/50" />
            <span className="ml-2 text-[12px] text-muted-foreground">Loading Pages…</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-2 py-8" data-testid="d90-error">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <span className="text-[12px] text-foreground/80">{error}</span>
            <Button size="sm" onClick={load} data-testid="d90-retry">Try again</Button>
          </div>
        ) : pages.length === 0 ? (
          <div className="text-[12px] text-muted-foreground text-center py-8" data-testid="d90-empty">
            No Pages connected yet. Connect Facebook above to get started.
          </div>
        ) : (
          <>
            {/* Toolbar: Select all matching filter */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-[11px]"
                onClick={toggleAllFiltered}
                data-testid="d90-select-all"
              >
                {allFilteredSelected ? 'Deselect all' : 'Select all'}
                <span className="ml-1 text-muted-foreground">({filtered.length})</span>
              </Button>
              <span className="text-[11px] text-muted-foreground" data-testid="d90-selection-counter">
                {selectedCount > 0
                  ? `${selectedCount} selected of ${pages.length}`
                  : `${pages.length} total · ${filtered.length} matching filter`}
              </span>
              {selectedCount > 0 && (
                <Button size="sm" variant="ghost" className="h-8 text-[11px] ml-auto" onClick={clearSelection}
                        data-testid="d90-clear-selection">
                  <X className="h-3 w-3 mr-1" /> Clear
                </Button>
              )}
            </div>

            {/* Bulk action bar (only when selection > 0) */}
            {selectedCount > 0 && (
              <div className="p-3 rounded-lg border border-gold/30 bg-gold/[0.04] flex flex-col sm:flex-row sm:items-center gap-2"
                   data-testid="d90-bulk-bar">
                <span className="text-[12px] text-foreground/90 font-medium">
                  {selectedCount} Page{selectedCount === 1 ? '' : 's'} →
                </span>
                <div className="flex-1 min-w-0 max-w-xs">
                  <StellarSelect
                    value={assignTarget}
                    onValueChange={(v) => { setAssignTarget(v); if (v !== '__create__') setNewCgName(''); }}
                    options={assignOptions}
                    placeholder="Pick a client group…"
                    testId="d90-cg-select"
                  />
                </div>
                {assignTarget === '__create__' && (
                  <Input
                    value={newCgName}
                    onChange={(e) => setNewCgName(e.target.value)}
                    placeholder="New client group name…"
                    className="h-8 text-[12px] max-w-xs"
                    data-testid="d90-new-cg-name"
                  />
                )}
                <Button
                  size="sm"
                  className="h-8 text-[11px]"
                  onClick={applyAssignment}
                  disabled={busy}
                  data-testid="d90-apply"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                  Apply
                </Button>
              </div>
            )}

            {/* Page list */}
            <div className="max-h-[28rem] overflow-y-auto border border-border/30 rounded-md divide-y divide-border/20"
                 data-testid="d90-page-list">
              {filtered.length === 0 ? (
                <div className="p-6 text-[12px] text-muted-foreground text-center">No Pages match your filter.</div>
              ) : filtered.map((p) => {
                if (!p?.id) return null;
                const cgName = p.friendly_name || (p.client_group_id && cgById[p.client_group_id]?.name) || '';
                const sel = !!selected[p.id];
                return (
                  <label key={p.id}
                         className="flex items-center gap-3 p-2.5 hover:bg-foreground/[0.02] cursor-pointer"
                         data-testid={`d90-page-row-${p.id}`}>
                    <input type="checkbox" checked={sel}
                           onChange={(e) => toggleRow(p.id, e.target.checked)}
                           className="h-4 w-4 accent-gold"
                           data-testid={`d90-page-check-${p.id}`} />
                    <div className="h-8 w-8 rounded-full bg-foreground/5 border border-border/40 overflow-hidden shrink-0 flex items-center justify-center">
                      {p?.picture_url
                        ? <img src={p.picture_url} alt="" className="h-full w-full object-cover" />
                        : <ImageIcon className="h-4 w-4 text-muted-foreground/40" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium truncate">{p?.name || 'Untitled Page'}</div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-2 truncate">
                        {p?.category && <span className="truncate">{p.category}</span>}
                        {p?.ig_username && (
                          <span className="inline-flex items-center gap-0.5 text-pink-400 shrink-0">
                            <Instagram className="h-3 w-3" />@{p.ig_username}
                          </span>
                        )}
                      </div>
                    </div>
                    {cgName ? (
                      <Badge variant="outline" className="text-[10px] bg-gold/10 border-gold/40 text-gold shrink-0"
                             data-testid={`d90-page-chip-${p.id}`}>
                        {cgName}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground/70 shrink-0"
                             data-testid={`d90-page-unassigned-${p.id}`}>
                        Unassigned
                      </Badge>
                    )}
                  </label>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
